import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { TextArea } from '@/components/base/textarea/textarea';
import type { CompanyFormStep1Data, CompanyType } from '@/types/company';
import { useTranslation } from 'react-i18next';

export type CompanyTypeOption = { id: string; label: string };
export type CountryOption = { id: string; label: string };
export type StatusOption = { id: string; label: string };

export type CompanyProfileStep1FieldsProps = {
    step1: CompanyFormStep1Data;
    updateStep1: (updates: Partial<CompanyFormStep1Data>) => void;
    updateStep1Address: (updates: Partial<CompanyFormStep1Data['address']>) => void;
    companyTypeOptions: CompanyTypeOption[];
    countryOptions: CountryOption[];
    statusOptions: StatusOption[];
    /** When true, all fields are disabled (e.g. operator viewing company). */
    readOnly?: boolean;
    /** Admin company form shows active/inactive; profile self-service may omit. */
    showStatusField?: boolean;
};

export function CompanyProfileStep1Fields({
    step1,
    updateStep1,
    updateStep1Address,
    companyTypeOptions,
    countryOptions,
    statusOptions,
    readOnly = false,
    showStatusField = true,
}: CompanyProfileStep1FieldsProps) {
    const { t } = useTranslation(['pages:companies', 'common']);

    return (
        <div className="space-y-6">
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.general')}</h2>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Select
                        label={t('form.fields.companyType')}
                        placeholder={t('form.fields.companyTypePlaceholder')}
                        items={companyTypeOptions}
                        selectedKey={step1.company_type}
                        onSelectionChange={(key) => updateStep1({ company_type: key as CompanyType })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Input
                        label={t('form.fields.companyName')}
                        placeholder={t('form.fields.companyNamePlaceholder')}
                        value={step1.company_name}
                        onChange={(value) => updateStep1({ company_name: value })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    />

                    <Input
                        label={t('form.fields.legalName')}
                        placeholder={t('form.fields.legalNamePlaceholder')}
                        value={step1.legal_name}
                        onChange={(value) => updateStep1({ legal_name: value })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    />

                    <Input
                        label={t('form.fields.taxId')}
                        placeholder={t('form.fields.taxIdPlaceholder')}
                        value={step1.tax_id}
                        onChange={(value) => updateStep1({ tax_id: value })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    />

                    {showStatusField && (
                        <Select
                            label={t('form.fields.status')}
                            placeholder={t('form.fields.statusPlaceholder')}
                            items={statusOptions}
                            selectedKey={String(step1.is_active)}
                            onSelectionChange={(key) => updateStep1({ is_active: key === 'true' })}
                            isRequired
                            size="md"
                            isDisabled={readOnly}
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    )}
                </div>
            </div>

            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.address')}</h2>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <Input
                            label={t('form.fields.street')}
                            placeholder={t('form.fields.streetPlaceholder')}
                            value={step1.address.street}
                            onChange={(value) => updateStep1Address({ street: value })}
                            isRequired
                            size="md"
                            isDisabled={readOnly}
                        />
                    </div>

                    <Input
                        label={t('form.fields.city')}
                        placeholder={t('form.fields.cityPlaceholder')}
                        value={step1.address.city}
                        onChange={(value) => updateStep1Address({ city: value })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    />

                    <Input
                        label={t('form.fields.state')}
                        placeholder={t('form.fields.statePlaceholder')}
                        value={step1.address.state}
                        onChange={(value) => updateStep1Address({ state: value })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    />

                    <Input
                        label={t('form.fields.postalCode')}
                        placeholder={t('form.fields.postalCodePlaceholder')}
                        value={step1.address.postalCode}
                        onChange={(value) => updateStep1Address({ postalCode: value })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    />

                    <Select
                        label={t('form.fields.country')}
                        placeholder={t('form.fields.countryPlaceholder')}
                        items={countryOptions}
                        selectedKey={step1.country_code}
                        onSelectionChange={(key) => updateStep1({ country_code: key as string })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.contacts')}</h2>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                        label={t('form.fields.email')}
                        type="email"
                        placeholder={t('form.fields.emailPlaceholder')}
                        value={step1.primary_email}
                        onChange={(value) => updateStep1({ primary_email: value })}
                        isRequired
                        size="md"
                        isDisabled={readOnly}
                    />

                    <Input
                        label={t('form.fields.phone')}
                        placeholder={t('form.fields.phonePlaceholder')}
                        value={step1.primary_phone}
                        onChange={(value) => updateStep1({ primary_phone: value })}
                        size="md"
                        isDisabled={readOnly}
                    />
                </div>
            </div>

            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.notes')}</h2>

                <TextArea
                    label={t('form.fields.notes')}
                    placeholder={t('form.fields.notesPlaceholder')}
                    value={step1.notes}
                    onChange={(value) => updateStep1({ notes: value })}
                    rows={4}
                    isDisabled={readOnly}
                />
            </div>
        </div>
    );
}
